import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import {
  Boton,
  Card,
  CardContenido,
  CardDescripcion,
  CardEncabezado,
  CardTitulo,
  Esqueleto,
  Pill,
  PuntoNaranja,
  Vacio,
  cn,
} from "@iavmx/ui";
import { leer } from "../api";
import { useSesion } from "../sesion";
import { fechaCorta, fechaLarga, primerNombre, relativa, saludo } from "../fechas";
import { NOMBRE_ORIGEN } from "../componentes";
import type { Aviso, Inicio as DatosInicio } from "../tipos";
import { nombreRolApp, type ClaveApp } from "../../compartido/apps";

export function Inicio() {
  const { yo, api } = useSesion();
  const inicio = useQuery({
    queryKey: ["inicio"],
    queryFn: () => api.api.inicio.$get().then((r) => leer<DatosInicio>(r)),
    refetchInterval: 60_000,
  });
  const avisos = useQuery({
    queryKey: ["avisos", "recientes"],
    queryFn: () => api.api.avisos.$get({ query: {} }).then((r) => leer<{ avisos: Aviso[]; sinLeer: number }>(r)),
  });
  if (!yo) return null;
  const d = inicio.data;
  const resumen = d
    ? `${d.total === 0 ? "Nada pendiente" : `${d.total} ${d.total === 1 ? "pendiente" : "pendientes"} en tus herramientas`}${d.vencenHoy ? `, ${d.vencenHoy} ${d.vencenHoy === 1 ? "vence" : "vencen"} hoy` : ""}${d.vencidas ? `, ${d.vencidas} ${d.vencidas === 1 ? "vencida" : "vencidas"}` : ""}`
    : "Cargando lo que te toca…";
  const listas = (d?.herramientas ?? yo.herramientas.map((h) => ({ ...h, pendientes: 0 }))).filter((h) => h.tiene);
  const pronto = (d?.herramientas ?? []).filter((h) => !h.disponible);

  return (
    <div className="space-y-8">
      <div className="border-b border-linea pb-4">
        <h1 className="font-titulo text-3xl font-black leading-none">
          {saludo()}, {primerNombre(yo.nombre)}
          <PuntoNaranja />
        </h1>
        <p className="mt-2 font-ui text-sm text-tinta-tenue" aria-live="polite">
          {fechaLarga()} · {resumen}
        </p>
      </div>

      <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-8">
          <section aria-labelledby="herr">
            <h2 id="herr" className="mb-3 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Tus herramientas</h2>
            <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
              {listas.map((h) => (
                <a key={h.clave} href={h.url} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Card className="flex h-full flex-col transition-colors group-hover:bg-fondo-suave">
                    <CardEncabezado className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitulo>{h.nombre}</CardTitulo>
                        {h.rol && <Pill>{nombreRolApp(h.app as ClaveApp, h.rol)}</Pill>}
                      </div>
                      <CardDescripcion>{h.frase}</CardDescripcion>
                    </CardEncabezado>
                    <CardContenido className="flex items-center justify-between font-ui text-sm">
                      <span className="text-tinta-tenue">
                        {h.pendientes ? `${h.pendientes} para hoy` : "Sin pendientes"}
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-azul">
                        Abrir <ArrowRight className="size-4" />
                      </span>
                    </CardContenido>
                  </Card>
                </a>
              ))}
              {pronto.map((h) => (
                <Card key={h.clave} className="flex h-full flex-col border-transparent bg-fondo-suave">
                  <CardEncabezado className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitulo className="text-tinta-3">{h.nombre}</CardTitulo>
                      <Pill tono="neutro" className="border-dashed">Próximamente</Pill>
                    </div>
                    <CardDescripcion>{h.frase}</CardDescripcion>
                  </CardEncabezado>
                  <CardContenido className="font-ui text-sm text-tinta-tenue">{h.paraQuien}</CardContenido>
                </Card>
              ))}
            </div>
            {listas.length === 0 && d && (
              <Vacio titulo="Todavía no tienes herramientas" texto="Quien te invitó puede dártelas desde Administración." className="mt-4" />
            )}
          </section>

          <section aria-labelledby="hoy">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h2 id="hoy" className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Lo que te toca hoy</h2>
              <span className="font-ui text-xs text-tinta-tenue">Junta tus tareas y tickets de todas las herramientas · se actualiza cada minuto</span>
            </div>
            {inicio.isLoading ? (
              <div className="space-y-2" aria-busy="true">
                {[0, 1, 2].map((i) => <Esqueleto key={i} className="h-10" />)}
              </div>
            ) : inicio.error ? (
              <Vacio titulo="No se pudo cargar" texto={inicio.error.message} />
            ) : d && d.pendientes.length === 0 ? (
              <Vacio titulo="Nada pendiente por hoy" texto="Cuando te asignen una tarea, un ticket o una próxima acción venza, aparece aquí." />
            ) : (
              <ul className="divide-y divide-linea border-y border-linea">
                {d?.pendientes.map((p) => (
                  <li key={p.id} className="grid gap-1 py-3 sm:grid-cols-[120px_1fr_auto] sm:gap-4">
                    <div><Pill tono="neutro">{NOMBRE_ORIGEN[p.origen]}</Pill></div>
                    <div className="min-w-0">
                      <a href={p.url} className="font-ui text-sm font-semibold hover:underline">{p.titulo}</a>
                      {p.detalle && <p className="font-ui text-xs text-tinta-tenue">{p.detalle}</p>}
                    </div>
                    <span className={cn("font-ui text-sm tabular-nums", (p.urgencia === "hoy" || p.urgencia === "vencida") ? "font-semibold text-rojo" : "text-tinta-2")}>
                      {p.urgencia === "hoy" ? "Vence hoy" : p.urgencia === "vencida" ? `Venció ${fechaCorta(p.vence!)}` : p.vence ? fechaCorta(p.vence) : "Sin fecha"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="bg-fondo-suave p-5" aria-labelledby="avisos-t">
          <div className="mb-2 flex items-center justify-between">
            <h2 id="avisos-t" className="font-titulo text-xl font-bold">Avisos</h2>
            {(avisos.data?.sinLeer ?? 0) > 0 && (
              <span className="rounded-full bg-naranja px-2.5 py-0.5 font-ui text-xs font-bold text-tinta">{avisos.data!.sinLeer} sin leer</span>
            )}
          </div>
          {avisos.isLoading ? (
            <Esqueleto className="h-24" />
          ) : !avisos.data || avisos.data.avisos.length === 0 ? (
            <p className="py-4 font-ui text-sm text-tinta-tenue">Sin avisos todavía. Cuando una campaña se atrase o alguien acepte tu invitación, aparece aquí.</p>
          ) : (
            <ul className="divide-y divide-linea">
              {avisos.data.avisos.slice(0, 6).map((a) => (
                <li key={a.id} className="grid grid-cols-[14px_1fr] gap-2 py-3">
                  <span aria-hidden="true" className={cn("mt-1.5 size-2 rounded-full", a.leidaEn ? "" : "bg-azul")} />
                  <div>
                    <p className={cn("font-ui text-sm", a.leidaEn ? "text-tinta-3" : "text-tinta")}>{a.titulo}</p>
                    <p className="font-ui text-xs text-tinta-tenue">{NOMBRE_ORIGEN[a.app] ?? a.app} · {relativa(a.creadoEn)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex items-center justify-between">
            <Boton variante="liga" asChild><Link to="/avisos">Ver todos los avisos</Link></Boton>
            {avisos.data && <span className="font-ui text-xs text-tinta-tenue">{avisos.data.avisos.length} en total</span>}
          </div>
        </aside>
      </div>
    </div>
  );
}
